import { COMPANY_ID } from 'utils/constants';

import { busmanApiSlice } from './busmanApiSlice';

export type SyncTaskState = 'PENDING' | 'STARTED' | 'PROGRESS' | 'RETRY' | 'SUCCESS' | 'FAILURE' | 'REVOKED';

export interface SyncTaskStart {
  task_id: string;
  state: SyncTaskState;
  message?: string;
  status_url?: string;
}

// Per date summary on a payroll task. `message` is only set when that date failed.
export interface SyncTaskDaySummary {
  date?: string;
  fetched?: number;
  filtered?: number;
  processed?: number;
  // The adjustment's counterpart to `processed`
  adjusted?: number;
  skipped?: number;
  failed?: number;
  sync_success?: boolean;
  message?: string | null;
}

/**
 * Terminal payload of a payroll task. `success` is the real verdict - a celery SUCCESS
 * carrying `success: false` still means the sweep did not complete. `skipped` is set when
 * another sync held the lock. `message` only exists on the early return paths (no MYOB
 * instance, no company id, lock held, unresolvable employee id).
 */
export interface SyncTaskResult {
  success?: boolean;
  skipped?: boolean;
  message?: string;
  total?: number;
  // Known keys are fetched, filtered, processed (adjusted on the adjustment), skipped and
  // failed - left open so a tally the backend adds still reaches the log
  counts?: Record<string, number>;
  employee_id?: string;
  start_date?: string;
  end_date?: string;
  results?: unknown[];
}

export interface SyncTaskStatus {
  task_id: string;
  state: SyncTaskState;
  message?: string;
  // Celery task meta - the payroll tasks report `current`/`total` flat, with no percent
  current?: number;
  total?: number;
  processed?: number;
  // The adjustment task's counterpart to `processed`
  adjusted?: number;
  failed?: number;
  current_date?: string;
  last_day?: SyncTaskDaySummary;
  // The adjustment task's counterpart to `last_day`
  last_adjustment?: SyncTaskDaySummary;
  // Older syncs may still report progress as a percent, flat or nested
  percent?: number;
  progress?: { current?: number; total?: number; percent?: number };
  result?: SyncTaskResult;
  error?: string;
}

export const myobEndpoints = busmanApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    //MYOB refresh token before start syncing
    //To be deleted as this is going to be handled at backend
    MYOBRefreshToken: builder.query({
      query: () => '/myob/refresh-token',
      transformResponse: (response: any) => response.data,
    }),
    //MYOB Sync
    syncWorklogsFromMYOB: builder.query({
      query: () => `/myob/timebilling/activityslips/?company_id=${COMPANY_ID}`,
      transformResponse: (response: any) => response.data,
    }),
    syncActivities: builder.query({
      query: () => `/myob/timebilling/activities/?company_id=${COMPANY_ID}`,
      transformResponse: (response: any) => response.data,
    }),
    syncCompanyFiles: builder.query({
      query: () => '/myob/companyfiles/',
      transformResponse: (response: any) => response.data,
    }),
    syncCustomers: builder.query({
      query: () => `/myob/customers/?company_id=${COMPANY_ID}`,
      transformResponse: (response: any) => response.data,
    }),

    // Kicks off the async employee sync and returns the celery task handle to poll
    syncEmployees: builder.query<SyncTaskStart, void>({
      query: () => `/myob/employees/?company_id=${COMPANY_ID}`,
      transformResponse: (response: any) => response.data,
    }),
    syncEmployeePolling: builder.query<SyncTaskStatus, string>({
      query: (taskId: string) => `/myob/employees/status/${taskId}/`,
      transformResponse: (response: any) => response.data,
      // Progress is transient - never serve a previous run's status from cache
      keepUnusedDataFor: 0,
    }),
    // Kicks off the async payroll sync and returns the celery task handle to poll
    syncEmployeePayroll: builder.query<SyncTaskStart, void>({
      query: () => `/myob/employees/payroll/?company_id=${COMPANY_ID}`,
      transformResponse: (response: any) => response.data,
    }),
    syncEmployeePayrollPolling: builder.query<SyncTaskStatus, string>({
      query: (taskId: string) => `/myob/employees/payroll/status/${taskId}/`,
      transformResponse: (response: any) => response.data,
      // Progress is transient - never serve a previous run's status from cache
      keepUnusedDataFor: 0,
    }),
    syncJobsFromMYOB: builder.query({
      query: () => `/myob/general/sync_jobs/?company_id=${COMPANY_ID}`,
      transformResponse: (response: any) => response.data,
    }),
    syncPayrollCategory: builder.query({
      query: () => `/myob/timebilling/payrollcategory/?company_id=${COMPANY_ID}`,
      transformResponse: (response: any) => response.data,
    }),
    syncJobsToMYOB: builder.mutation({
      query: () => {
        return {
          url: '/myob/general/sync_jobs/',
          method: 'post',
          body: { company_id: COMPANY_ID },
        };
      },
    }),
    updateJobsInMYOB: builder.mutation({
      query: () => {
        return {
          url: '/myob/general/update_jobs_in_myob/',
          method: 'post',
          body: { company_id: COMPANY_ID },
        };
      },
    }),
    /**
     * Kicks off the async worklog push and returns the celery task handle to poll. One
     * task covers the whole range for every employee - the unit of work is a worklog, not
     * an employee day - so `user_id` is only sent when one employee was picked.
     * Note this endpoint reads the employee from `user_id` in the body, while the two
     * payroll endpoints below read `employee_id` from the query string. All three mean
     * `EEAEmployee.user_id`.
     */
    syncWorklogsToMYOB: builder.mutation<SyncTaskStart, { start_date: string; end_date: string; user_id?: string }>({
      query: (body) => {
        return {
          url: '/myob/timebilling/sync_worklogs/',
          method: 'post',
          body: { ...body, company_id: COMPANY_ID },
        };
      },
      transformResponse: (response: any) => response.data,
    }),
    syncWorklogsToMYOBPolling: builder.query<SyncTaskStatus, string>({
      query: (taskId: string) => `/myob/timebilling/sync_worklogs/status/${taskId}/`,
      transformResponse: (response: any) => response.data,
      // Progress is transient - never serve a previous run's status from cache
      keepUnusedDataFor: 0,
    }),
    /**
     * Kicks off the async timesheet pull and returns the celery task handle to poll. One
     * task sweeps the whole range - it iterates the dates itself, one MYOB read per date.
     * `employee_id` narrows only the write back, not the read, so it saves the
     * delete/recreate for everyone else but leaves their mirrored rows stale until the
     * next full pull. Omit it to sweep everyone.
     */
    syncTimeSheets: builder.query<SyncTaskStart, { start_date: string; end_date: string; employee_id?: string }>({
      query: ({ start_date, end_date, employee_id }) => {
        const params = new URLSearchParams({ company_id: COMPANY_ID, start_date, end_date });
        if (employee_id) params.append('employee_id', employee_id);

        return `/myob/payroll/timesheets/?${params.toString()}`;
      },
      transformResponse: (response: any) => response.data,
    }),
    syncTimeSheetsPolling: builder.query<SyncTaskStatus, string>({
      query: (taskId: string) => `/myob/payroll/timesheets/status/${taskId}/`,
      transformResponse: (response: any) => response.data,
      // Progress is transient - never serve a previous run's status from cache
      keepUnusedDataFor: 0,
    }),
    // Kicks off the async timesheet adjustment and returns the celery task handle to poll.
    // One task covers the whole range for every employee - it is date major internally and
    // caches each date's read - so `employee_id` is only sent when one employee was picked.
    adjustTimeSheets: builder.query<SyncTaskStart, { start_date: string; end_date: string; employee_id?: string }>({
      query: ({ start_date, end_date, employee_id }) => {
        const params = new URLSearchParams({ company_id: COMPANY_ID, start_date, end_date });
        if (employee_id) params.append('employee_id', employee_id);

        return `/myob/payroll/timesheets/adjustment/?${params.toString()}`;
      },
      transformResponse: (response: any) => response.data,
    }),
    adjustTimeSheetsPolling: builder.query<SyncTaskStatus, string>({
      query: (taskId: string) => `/myob/payroll/timesheets/adjustment/status/${taskId}/`,
      transformResponse: (response: any) => response.data,
      // Progress is transient - never serve a previous run's status from cache
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useLazyAdjustTimeSheetsQuery,
  useLazyAdjustTimeSheetsPollingQuery,
  useLazySyncTimeSheetsQuery,
  useLazySyncTimeSheetsPollingQuery,
  useLazySyncActivitiesQuery,
  useLazySyncWorklogsFromMYOBQuery,
  useLazySyncCompanyFilesQuery,
  useLazySyncCustomersQuery,
  useLazySyncEmployeePayrollQuery,
  useSyncEmployeePayrollPollingQuery,
  useLazySyncEmployeesQuery,
  useSyncEmployeePollingQuery,
  useLazySyncJobsFromMYOBQuery,
  useLazySyncPayrollCategoryQuery,
  useLazyMYOBRefreshTokenQuery,
  useSyncJobsToMYOBMutation,
  useSyncWorklogsToMYOBMutation,
  useLazySyncWorklogsToMYOBPollingQuery,
  useUpdateJobsInMYOBMutation,
} = myobEndpoints;
